<template>
  <div class="regform-wrapper">
    <div class="regform-content">
      <div class="regform-logo pb-2">
        <img class="h-6" src="../assets/images/engage.svg" />
      </div>
      <div class="regform-title">
        <h4 class="title">Finish Creating Account</h4>
        <span v-if="loginError !== 'Error:  You must login to continue'" class="login-error-msg">{{
          loginError
        }}</span>
      </div>
      <div class="regform-row-ct">
        <v-text-field
          v-model="user.first_name"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="First Name"
          placeholder="John"
          type="text"
          name="first-name"
          autocomplete="given-name"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
        <v-text-field
          v-model="user.last_name"
          persistentPlaceholder
          placeholder="Chapman"
          variant="outlined"
          density="compact"
          hideDetails
          label="Last Name"
          type="text"
          name="last-name"
          autocomplete="family-name"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
      </div>
      <div class="regform-row-ct">
        <v-text-field
          v-model="user.email"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="Work Email"
          placeholder="John.chapman@company.com"
          type="email"
          name="email"
          autocomplete="email"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
        <v-text-field
          v-model="user.metadata.company"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="Company Name"
          placeholder="Company Inc."
          type="text"
          name="organization"
          autocomplete="organization"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
      </div>
      <div class="form-short-field">
        <v-text-field
          v-model="user.metadata.phone_number"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="Phone Number"
          placeholder="(310) 123-3456"
          type="text"
          name="tel"
          autocomplete="tel"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
      </div>
      <div class="regform-divider-line"></div>
      <div class="regform-row-ct">
        <v-text-field
          v-model="app.name"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="App Name"
          placeholder="Company App"
          type="text"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
        <v-text-field
          v-model="app.domain"
          persistentPlaceholder
          variant="outlined"
          density="compact"
          hideDetails
          label="App Domain Name"
          placeholder="yourappdomain.com"
          type="text"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
      </div>
      <div class="form-short-field">
        <v-select
          id="timezone_offset"
          v-model="app.timezone_offset"
          variant="outlined"
          density="compact"
          persistentPlaceholder
          hideDetails
          persistentHint
          hint="Timezone where the app business is based."
          name="timezone_offset"
          :items="Object.keys(timezones)"
          label="App Timezone"
        ></v-select>
      </div>
      <div class="regform-divider-line"></div>
      <v-form ref="passwordForm" v-model="isValid" class="form-short-field">
        <v-text-field
          v-model="user.password"
          class="regform-field-padding pb-0"
          variant="outlined"
          density="compact"
          label="Password"
          persistentPlaceholder
          prependInnerIcon="md:lock_outline"
          :type="showPassword ? 'text' : 'password'"
          :appendInnerIcon="showPassword ? 'md:visibility' : 'md:visibility_off'"
          name="password"
          autocomplete="new-password"
          :rules="passwordRules()"
          @click:appendInner="showPassword = !showPassword"
          @update:modelValue="$refs.passwordForm.validate()"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
        <v-text-field
          ref="confirm"
          v-model="user.confirmation"
          class="regform-confirm-pswd"
          variant="outlined"
          density="compact"
          persistentPlaceholder
          :type="showConfirmPassword ? 'text' : 'password'"
          :appendInnerIcon="showConfirmPassword ? 'md:visibility' : 'md:visibility_off'"
          :rules="passwordConfirmRules(() => user.password)"
          label="Confirm Password"
          prependInnerIcon="md:lock_outline"
          name="password-confirm"
          autocomplete="new-password"
          @click:appendInner="showConfirmPassword = !showConfirmPassword"
          @keyup="resetError"
          @keyup.enter="onRegister"
        ></v-text-field>
      </v-form>
    </div>
    <div class="regform-action-footer">
      <v-btn variant="flat" size="x-large" color="accent" :disabled="!isValid" @click="onRegister">
        Submit
      </v-btn>
    </div>
  </div>
</template>
<script>
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { passwordConfirmRules, passwordRules } from "@/utils/PasswordUtils";

export default {
  name: "RfRegister",
  components: {},
  props: ["onSubmit", "success"],
  setup: () => ({
    passwordRules,
    passwordConfirmRules,
    legacyLastErrorStore: useLegacyLastErrorStore(),
  }),
  data() {
    return {
      user: {
        email: "",
        first_name: "",
        last_name: "",
        password: null,
        confirmation: null,
        metadata: {
          phone_number: "",
          company: "",
        },
      },
      error: null,
      showError: false,
      app: {
        name: "",
        domain: "",
        timezone_offset: "Pacific Time (US & Canada)",
      },
      timezones: {
        "Pacific Time (US & Canada)": "Pacific Time",
        "Mountain Time (US & Canada)": "Mountain Time",
        "Central Time (US & Canada)": "Central Time",
        "Eastern Time (US & Canada)": "Eastern Time",
        "Alaska": "Alaska",
        "Hawaii": "Hawaii",
      },
      urlParams: {},
      showPassword: false,
      showConfirmPassword: false,
      isValid: false,
    };
  },
  computed: {
    loginError() {
      if (this.showError) {
        return this.error || this.displayError(this.legacyLastErrorStore.lastError);
      }
      return null;
    },
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlParams);
    if (params && params.email) {
      this.user.email = params.email;
    }
  },
  methods: {
    onRegister() {
      this.showError = true;
      if (
        !this.user.email.length ||
        !this.user.first_name.length ||
        !this.user.metadata.phone_number.length ||
        !this.user.metadata.company.length ||
        !this.app.name.length ||
        !this.app.domain.length
      ) {
        this.error = "All fields must be filled in";
        return;
      }
      if (
        this.user.password &&
        this.user.confirmation &&
        this.user.confirmation === this.user.password
      ) {
        try {
          this.$emit("submit", {
            user: this.user,
            app: this.app,
          });
        } catch (e) {
          this.error = e;
          this.showError = true;
        }
      } else {
        this.error = "Passwords must match!";
      }
    },
    resetError() {
      this.error = null;
      this.showError = false;
    },
    displayError(error) {
      console.log(error);
      const message = error
        .toString()
        .replace(/[_\[]/g, " ")
        .replace(/^Error:/, "")
        .replace(/[\]"{}:]/g, "");

      return `Error: ${message}`;
    },
  },
};
</script>

<style scoped>
.login-error-msg {
  color: #c00;
  display: block;
  text-align: left;
  padding: 0.5rem 0;
  font-size: 1rem;
}
</style>
